import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import addEvent from 'utils/addEvent';
import ExportPortfolioCsv from 'component/ExportPortfolioCsv';
import ActivityRow from './ActivityRow';
import ActivityErrorBoundary from './ActivityErrorBoundary';
import {
    ACTIVITY_FILTER_OPTIONS,
    getActivitySymbol,
    PORTFOLIO_ACTIVITY_TYPES,
} from './activityHelpers';

export const Activity = () => {
    const [transaction, setTransaction] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showActivities, setShowActivities] = useState(PORTFOLIO_ACTIVITY_TYPES);
    const [symbolQuery, setSymbolQuery] = useState('');
    const [expandedId, setExpandedId] = useState(null);
    const navigateTo = useNavigate();

    useEffect(() => {
        setLoading(true);
        setError(null);
        fetch('/api/v1/user/activity')
            .then((res) => {
                if (!res.ok) throw new Error('Failed to load activity');
                return res.json();
            })
            .then((res) => setTransaction(Array.isArray(res) ? res.filter((item) => item && item.activity_type) : []))
            .catch((err) => setError(err.message || 'Failed to load activity'))
            .finally(() => setLoading(false));

        addEvent('View Activity Page');
    }, []);

    const toggleFilter = (key) => {
        setShowActivities((prev) =>
            prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
        );
    };

    const filteredRows = useMemo(() => {
        const query = symbolQuery.trim().toUpperCase();
        return transaction.filter((item) => {
            if (!showActivities.includes(item.activity_type)) return false;
            if (!query) return true;
            const symbol = getActivitySymbol(item);
            return symbol?.includes(query);
        });
    }, [transaction, showActivities, symbolQuery]);

    const toggleExpanded = (id) => {
        setExpandedId((prev) => (prev === id ? null : id));
    };

    return (
        <ActivityErrorBoundary>
        <Box sx={{ px: { xs: 1, md: 2 }, pb: 6, pt: 2 }}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', lg: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'stretch', lg: 'center' },
                    gap: 2,
                    mb: 2,
                }}
            >
                <Box>
                    <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700 }}>
                        Activity History
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 14, mt: 0.5 }}>
                        Portfolio ledger — expand any row for before/after details
                    </Typography>
                </Box>
                <Button
                    onClick={() => navigateTo('/dashboard/main')}
                    sx={{ color: '#fff', px: 3, borderColor: '#fff', borderRadius: '20px', alignSelf: { xs: 'flex-start', lg: 'center' } }}
                    variant="outlined"
                >
                    Home
                </Button>
            </Box>

            <Paper
                elevation={0}
                sx={{
                    bgcolor: 'primary.light',
                    borderRadius: 2,
                    p: { xs: 1.5, md: 2 },
                    mb: 2,
                    boxShadow: '0px 0px 2px #fff',
                }}
            >
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', flex: 1 }}>
                    <TextField
                        size="small"
                        placeholder="Search symbol (e.g. BTC)"
                        value={symbolQuery}
                        onChange={(e) => setSymbolQuery(e.target.value.toUpperCase())}
                        sx={{
                            minWidth: 220,
                            flexShrink: 0,
                            '& .MuiOutlinedInput-root': {
                                color: '#fff',
                                bgcolor: '#21232D',
                                height: 36,
                            },
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'grey' },
                        }}
                    />
                    <Button
                        size="small"
                        onClick={() => setShowActivities(PORTFOLIO_ACTIVITY_TYPES)}
                        sx={{ color: '#fff', textTransform: 'none', flexShrink: 0 }}
                    >
                        Portfolio only
                    </Button>
                    <Button
                        size="small"
                        onClick={() => setShowActivities(ACTIVITY_FILTER_OPTIONS.map((o) => o.key))}
                        sx={{ color: '#fff', textTransform: 'none', flexShrink: 0 }}
                    >
                        Show all types
                    </Button>
                    {ACTIVITY_FILTER_OPTIONS.map(({ key, label }) => (
                        <Chip
                            key={key}
                            label={label}
                            clickable
                            onClick={() => toggleFilter(key)}
                            sx={{
                                color: '#fff',
                                borderColor: showActivities.includes(key) ? '#95F205' : 'rgba(255,255,255,0.35)',
                                bgcolor: showActivities.includes(key) ? 'rgba(149, 242, 5, 0.12)' : 'transparent',
                                fontWeight: 600,
                                height: 32,
                            }}
                            variant="outlined"
                        />
                    ))}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, ml: 'auto' }}>
                        <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 13 }}>
                            {filteredRows.length} event{filteredRows.length === 1 ? '' : 's'}
                        </Typography>
                        <ExportPortfolioCsv />
                    </Box>
                </Box>
            </Paper>

            <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                    bgcolor: 'primary.light',
                    borderRadius: 2,
                    maxHeight: 'calc(100vh - 280px)',
                    boxShadow: '0px 0px 2px #fff',
                }}
            >
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress sx={{ color: '#fff' }} />
                    </Box>
                ) : error ? (
                    <Box sx={{ py: 6, px: 2, textAlign: 'center' }}>
                        <Typography sx={{ color: '#f87171', mb: 1 }}>{error}</Typography>
                        <Button variant="outlined" sx={{ color: '#fff', borderColor: '#fff' }} onClick={() => window.location.reload()}>
                            Retry
                        </Button>
                    </Box>
                ) : (
                    <Table stickyHeader size="small" sx={{ minWidth: 900 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ color: '#fff', fontWeight: 700, bgcolor: 'primary.light', py: 1 }}>#</TableCell>
                                <TableCell sx={{ color: '#fff', fontWeight: 700, bgcolor: 'primary.light', py: 1 }}>Date</TableCell>
                                <TableCell sx={{ color: '#fff', fontWeight: 700, bgcolor: 'primary.light', py: 1 }}>Type</TableCell>
                                <TableCell sx={{ color: '#fff', fontWeight: 700, bgcolor: 'primary.light', py: 1 }}>Asset</TableCell>
                                <TableCell sx={{ color: '#fff', fontWeight: 700, bgcolor: 'primary.light', py: 1 }}>Summary</TableCell>
                                <TableCell sx={{ color: '#fff', fontWeight: 700, bgcolor: 'primary.light', py: 1, width: 48 }} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredRows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} sx={{ color: 'rgba(255,255,255,0.65)', py: 6, textAlign: 'center' }}>
                                        No activity matches your filters.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredRows.map((element, index) => (
                                    <ActivityRow
                                        key={element._id}
                                        activity={element}
                                        index={index}
                                        expanded={expandedId === element._id}
                                        onToggle={() => toggleExpanded(element._id)}
                                    />
                                ))
                            )}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>
        </Box>
        </ActivityErrorBoundary>
    );
};

export default Activity;
